import Flatpickr from 'react-flatpickr';
import { Image } from '@chakra-ui/react';
import React, { useRef, useEffect } from 'react';
import 'flatpickr/dist/themes/material_blue.css';
import {today} from '@internationalized/date';
import { fixTimezoneOffset } from '@/queue/utils';

export interface FlatpickrPropsInterface {
    label: string;
    value: any;
    classNameValue?: string;
    onDateChange(args: any): void;
    options?: any;
}

const FlatDatePickr = ({ label, value, classNameValue, onDateChange, options }: FlatpickrPropsInterface) => {
    const flatpickrInstance = useRef<any>(null);

    const {
        disabled = false,
        minDate = null,
        maxDate = null,
        placeholderText = "mm/dd/yyyy",
        timeZone = null
    } = options || {};

    // Calculate maxDate based on timeZone if provided
    const calculatedMaxDate = timeZone ? today(timeZone) : maxDate;

    const normalizeValue = (val: any) => {
        if (!val) return null;

        if (val && typeof val === 'object' && 'year' in val && 'month' in val && 'day' in val) {
            return new Date(val?.year, val?.month - 1, val?.day);
        }

        if (val instanceof Date) return val;

        if (typeof val === 'string') {
            // ✅ Support YYYY-MM-DD
            let match = val?.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
            if (match) {
                const year = parseInt(match[1], 10);
                const month = parseInt(match[2], 10) - 1;
                const day = parseInt(match[3], 10);
                return new Date(year, month, day);
            }

            // ✅ Support MM/DD/YYYY (your current getFormattedDate output)
            match = val?.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (match) {
                const month = parseInt(match[1], 10) - 1;
                const day = parseInt(match[2], 10);
                const year = parseInt(match[3], 10);
                return new Date(year, month, day);
            }

            return null; // safer fallback
        }

        return val;
    };

    const normalizedValue = normalizeValue(value);
    // Always pass a defined value to avoid uncontrolled→controlled React warning (use [] when empty)
    const valueForInput = normalizedValue != null ? normalizedValue : [];

    // ✅ After Flatpickr is mounted, restrict date selection dynamically
    useEffect(() => {
        if (flatpickrInstance?.current) {
            const disabledDates: any[] = [];

            // Handle minDate restriction
            if (minDate) {
                const minDateObj = minDate === 'today' 
                    ? new Date() 
                    : new Date(minDate);
                minDateObj?.setHours(0, 0, 0, 0);

                // Disable all dates before minDate
                disabledDates.push((date: Date) => {
                    const checkDate = new Date(date);
                    checkDate?.setHours(0, 0, 0, 0);
                    return checkDate < minDateObj;
                });
            }

            // Handle maxDate restriction
            if (calculatedMaxDate) {
                let maxDateObj;
                
                // If calculatedMaxDate is from today(timeZone), convert to JS Date
                if (calculatedMaxDate && typeof calculatedMaxDate === 'object' && 'year' in calculatedMaxDate) {
                    maxDateObj = new Date(calculatedMaxDate.year, calculatedMaxDate.month - 1, calculatedMaxDate.day);
                } else if (calculatedMaxDate === 'today') {
                    maxDateObj = new Date();
                } else {
                    maxDateObj = new Date(calculatedMaxDate);
                }
                
                maxDateObj?.setHours(0, 0, 0, 0);

                // Disable all dates after maxDate
                disabledDates.push((date: Date) => {
                    const checkDate = new Date(date);
                    checkDate?.setHours(0, 0, 0, 0);
                    return checkDate > maxDateObj;
                });
            }

            // Apply restrictions
            flatpickrInstance?.current?.set('disable', disabledDates);
            flatpickrInstance?.current?.redraw();
        }
    }, [minDate, calculatedMaxDate, value]);

    useEffect(() => {
        if (!flatpickrInstance?.current) return;
        if (value != null && value !== '') {
            const normalized = fixTimezoneOffset(value);
            const current = flatpickrInstance.current?.selectedDates?.[0];
            if (!current || normalized?.getTime() !== current?.getTime()) {
                flatpickrInstance.current?.setDate(normalized, false);
            }
        } else {
            flatpickrInstance.current?.setDate([], false);
        }
    }, [value]);


    const handleIconClick = () => {
        if (flatpickrInstance?.current && !disabled) {
            flatpickrInstance.current?.open();
        }
    };

    return (
        <div>
            {label && <div className="date-label">{label}</div>}
            <div className={`date-picker-wrapper ${disabled ? 'disabled' : ''}`}>
                <div className={`date-input-group ${disabled ? 'disabled' : ''}`}>
                    <Flatpickr
                        value={valueForInput}
                        onChange={([date]: any) => {
                            if (!disabled) {
                                onDateChange(date);
                            }
                        }}
                        options={{
                            dateFormat: 'm-d-Y',
                            allowInput: !disabled, 
                            clickOpens: !disabled,
                            enableTime: false,
                            altInput: true,

                            // 🔥 FIX: Add monthSelectorType for dropdown
                            monthSelectorType: 'dropdown',

                            // 🔥 FIX: Prevent closing when clicking month/year dropdowns
                            closeOnSelect: true, // Keep true for date selection

                            // 🔥 Alternative approach using ignoredFocusElements
                            ignoredFocusElements: [
                                document.querySelector('.flatpickr-monthDropdown-months'),
                                document.querySelector('.numInput.cur-year'),
                                document.querySelector('.flatpickr-prev-month'),
                                document.querySelector('.flatpickr-next-month')
                            ].filter(Boolean),

                            // Keep calendar static when interacting with controls
                            static: false,

                            // 🔥 FIX: Prevent closing but allow functionality
                            onOpen: function (selectedDates, dateStr, instance) {
                                setTimeout(() => {
                                    const calendarContainer = instance.calendarContainer;

                                    // Make month/year controls work without closing
                                    const monthsContainer = calendarContainer.querySelector('.flatpickr-months');
                                    if (monthsContainer) {
                                        monthsContainer.addEventListener('click', (e) => {
                                            // Prevent the click from reaching document level
                                            e.stopPropagation();

                                            // Allow default behavior for the controls themselves
                                            const target: any = e.target;

                                            // Handle year increment/decrement
                                            if (target.classList.contains('arrowUp')) {
                                                instance.changeYear(instance.currentYear + 1);
                                            } else if (target.classList.contains('arrowDown')) {
                                                instance.changeYear(instance.currentYear - 1);
                                            }
                                            // Handle month navigation
                                            else if (target.closest('.flatpickr-prev-month')) {
                                                instance.changeMonth(-1);
                                            } else if (target.closest('.flatpickr-next-month')) {
                                                instance.changeMonth(1);
                                            }
                                        }, true);
                                    }
                                }, 10);
                            },

                            ...(calculatedMaxDate && { 
                                maxDate: calculatedMaxDate && typeof calculatedMaxDate === 'object' && 'year' in calculatedMaxDate
                                    ? new Date(calculatedMaxDate.year, calculatedMaxDate.month - 1, calculatedMaxDate.day)
                                    : calculatedMaxDate
                            }),
                            formatDate: (date) => {
                                const month = String(date?.getMonth() + 1)?.padStart(2, '0');
                                const day = String(date?.getDate())?.padStart(2, '0');
                                const year = date?.getFullYear();
                                return `${month}/${day}/${year}`;
                            },
                            parseDate: (dateInput: string | Date): Date | null => {
                                if (!dateInput) return null;

                                // Handle direct Date objects
                                if (dateInput instanceof Date && !isNaN(dateInput?.getTime())) return dateInput;

                                // Handle both "MM/DD/YYYY" or "MM-DD-YYYY"
                                if (typeof dateInput === 'string') {
                                    const match = dateInput?.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
                                    if (match) {
                                        const month = parseInt(match[1], 10) - 1;
                                        const day = parseInt(match[2], 10);
                                        const year = parseInt(match[3], 10);
                                        return new Date(year, month, day);
                                    }
                                }
                                // As a fallback — try to build a Date safely
                                try {
                                    const fallbackDate = new Date(dateInput as any);
                                    return isNaN(fallbackDate?.getTime()) ? null : fallbackDate;
                                } catch {
                                    return null;
                                }
                            },
                            onReady: (selectedDates, dateStr, instance) => {
                                flatpickrInstance.current = instance;
                            },
                        }}
                        className={`${classNameValue} ${disabled ? 'disabled' : ''}`}
                        placeholder={placeholderText}
                        disabled={disabled}
                    />
                    <div
                        className={`calendar-icon ${disabled ? 'disabled' : ''}`}
                        onClick={handleIconClick}
                        style={{
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <Image src="/images/ui/calendar-primary.svg" width="24px" height="24px" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlatDatePickr;
